// client/src/components/pages/RouteIntelligence/PreloadAlertLogModal.tsx (converted from .js, ST-00216; load errors shown, ST-00229)
import { useState, useEffect, useCallback } from 'react';
import { X, Mail, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../../../services/api';
import { formatDate } from './preloadConfig';
import type {
  AlertLogMonitor,
  PreloadAlertLogRow,
} from '../../../types/clientPages/routeIntelligence';

interface PreloadAlertLogModalProps {
  monitor: AlertLogMonitor;
  onClose: () => void;
}

const PreloadAlertLogModal = ({ monitor, onClose }: PreloadAlertLogModalProps) => {
  const [alerts, setAlerts] = useState<PreloadAlertLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  // ST-00229: a failed load used to be swallowed and read "No alerts sent yet."
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.preloadEvaluation.getAlertLog(monitor.monitor_id);
      setAlerts((res.data as PreloadAlertLogRow[]) || []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [monitor.monitor_id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary-500" /> Preload Alert History
            </h2>
            <p className="text-[11px] text-gray-400 mt-0.5">{monitor.grouping_name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-500" />
            </div>
          ) : failed ? (
            <div className="text-center py-16" role="alert">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-300" />
              <p className="text-xs text-red-600">Could not load the alert history.</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
              >
                Retry
              </button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-16">
              <Mail className="w-8 h-8 mx-auto mb-2 text-gray-200" />
              <p className="text-xs text-gray-400">No alerts sent yet.</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b border-gray-200">
                  {['Date', 'Preloads', 'Recipients', 'Status', 'Sent'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i === 3 ? 'text-center' : i === 4 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr
                    key={a.alert_log_id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 py-2 text-slate-800 font-medium">
                      {formatDate(a.alert_date)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-red-600 font-semibold">{a.preload_count}</span>
                    </td>
                    <td
                      className="px-3 py-2 text-gray-400 max-w-[160px] truncate"
                      title={a.recipients}
                    >
                      {a.recipients}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {a.status === 'sent' ? (
                        <span className="inline-flex items-center gap-1 text-primary-500 text-[10px] font-semibold">
                          <CheckCircle className="w-3 h-3" /> Sent
                        </span>
                      ) : a.status === 'partial' ? (
                        // Some recipients got it, some did not (ST-00215).
                        <span
                          className="inline-flex items-center gap-1 text-amber-600 text-[10px] font-semibold"
                          title={a.error_message ?? undefined}
                        >
                          <AlertTriangle className="w-3 h-3" /> Partial
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-red-500 text-[10px] font-semibold"
                          title={a.error_message ?? undefined}
                        >
                          <AlertTriangle className="w-3 h-3" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 text-[10px]">
                      {new Date(a.sent_at).toLocaleString('en-ZA')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreloadAlertLogModal;
